import { useEffect, useRef, useState, useCallback } from 'react';
import type { DriveFile, DriveFolder } from '../types';
import * as api from '../api/storageApi';
import { useMarqueeSelection } from '../hooks/useMarqueeSelection';

// ─── Helpers ──────────────────────────────────────────────────────
function isImageFile(name: string): boolean {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext);
}

function isPdfFile(name: string): boolean {
  return name.toLowerCase().endsWith('.pdf');
}

function getFileType(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  const typeMap: Record<string, string> = {
    pdf: 'PDF',
    doc: 'Word',
    docx: 'Word',
    xls: 'Excel',
    xlsx: 'Excel',
    ppt: 'PowerPoint',
    pptx: 'PowerPoint',
    jpg: 'Imagen',
    jpeg: 'Imagen',
    png: 'Imagen',
    gif: 'Imagen',
    svg: 'Imagen',
    webp: 'Imagen',
    zip: 'Archivo',
    rar: 'Archivo',
    mp4: 'Video',
    mp3: 'Audio',
    txt: 'Texto',
    csv: 'CSV',
    json: 'JSON',
  };
  return typeMap[ext] || 'Archivo';
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fileIcon(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  const map: Record<string, string> = {
    pdf: 'bi-file-earmark-pdf text-danger',
    doc: 'bi-file-earmark-word text-primary',
    docx: 'bi-file-earmark-word text-primary',
    xls: 'bi-file-earmark-excel text-success',
    xlsx: 'bi-file-earmark-excel text-success',
    ppt: 'bi-file-earmark-slides text-warning',
    pptx: 'bi-file-earmark-slides text-warning',
    jpg: 'bi-file-earmark-image text-info',
    jpeg: 'bi-file-earmark-image text-info',
    png: 'bi-file-earmark-image text-info',
    gif: 'bi-file-earmark-image text-info',
    zip: 'bi-file-earmark-zip text-secondary',
    rar: 'bi-file-earmark-zip text-secondary',
    mp4: 'bi-file-earmark-play text-purple',
    mp3: 'bi-file-earmark-music text-purple',
    txt: 'bi-file-earmark-text text-body-secondary',
    csv: 'bi-file-earmark-spreadsheet text-success',
    json: 'bi-file-earmark-code text-warning',
  };
  return map[ext] ?? 'bi-file-earmark text-body-secondary';
}

// ─── Folder card ──────────────────────────────────────────────────
interface FolderCardProps {
  folder: DriveFolder;
  selected: boolean;
  onOpen: () => void;
  onSelect: (e: React.MouseEvent) => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  isDragOver?: boolean;
}

function FolderCard({ folder, selected, onOpen, onSelect, onContextMenu, onDragStart, onDragEnd, onDragOver, onDragLeave, onDrop, isDragOver }: FolderCardProps) {
  return (
    <div
      className={`feebbo-card rounded-3 d-flex align-items-center gap-2 ${selected ? 'selected' : ''} ${isDragOver ? 'drag-over' : ''}`}
      style={{ 
        cursor: 'pointer', 
        userSelect: 'none', 
        overflow: 'hidden', 
        padding: '8px 12px',
        minHeight: 48,
        outline: isDragOver ? '2px dashed #2563eb' : 'none', 
        outlineOffset: -2 
      }}
      data-path={folder.path}
      data-item
      draggable
      onDoubleClick={onOpen}
      onClick={onSelect}
      onContextMenu={onContextMenu}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <i className="bi bi-folder-fill" style={{ fontSize: 20, color: '#f0ad4e', flexShrink: 0 }} />
      <p className="small text-truncate mb-0 fw-medium" style={{ color: 'var(--feebbo-text)', fontSize: '0.875rem' }} title={folder.name}>
        {folder.name}
      </p>
    </div>
  );
}

// ─── File card with preview ──────────────────────────────────────
interface FileCardProps {
  file: DriveFile;
  selected: boolean;
  onDownload: () => void;
  onOpen: () => void;
  onSelect: (e: React.MouseEvent) => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
}

function FileCard({ file, selected, onOpen, onSelect, onContextMenu, onDragStart, onDragEnd }: FileCardProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState(false);
  const isImage = isImageFile(file.name);
  const isPdf = isPdfFile(file.name);

  const loadPreview = async () => {
    if (!isImage || previewUrl || previewError) return;
    try {
      const url = await api.getDownloadUrl(file.path, false); // No forzar descarga para preview
      setPreviewUrl(url);
    } catch (e) {
      setPreviewError(true);
    }
  };

  return (
    <div
      className={`feebbo-card rounded-3 h-100 ${selected ? 'selected' : ''}`}
      style={{ cursor: 'pointer', userSelect: 'none', overflow: 'hidden', minHeight: 220 }}
      data-path={file.path}
      data-item
      draggable
      onClick={onSelect}
      onDoubleClick={onOpen}
      onContextMenu={onContextMenu}
      onMouseEnter={loadPreview}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      {/* Preview area */}
      <div 
        className="d-flex align-items-center justify-content-center" 
        style={{ 
          height: 140, 
          background: isImage ? '#f8f9fa' : 'transparent',
          borderBottom: '1px solid var(--feebbo-border)'
        }}
      >
        {isImage && previewUrl && !previewError ? (
          <img 
            src={previewUrl} 
            alt={file.name}
            style={{ 
              width: 28,
              height: 28,
              objectFit: 'cover',
              borderRadius: 8
            }}
            onError={() => setPreviewError(true)}
          />
        ) : isPdf ? (
          <div className="text-center">
            <i className="bi bi-file-earmark-pdf text-danger" style={{ fontSize: 48 }} />
            <div className="small text-muted mt-1">PDF</div>
          </div>
        ) : (
          <i className={`bi ${fileIcon(file.name)}`} style={{ fontSize: 48 }} />
        )}
      </div>
      
      {/* Info area */}
      <div className="p-2">
        <p className="small text-truncate mb-0 fw-medium" style={{ color: 'var(--feebbo-text)' }} title={file.name}>
          {file.name}
        </p>
        <div className="d-flex justify-content-between align-items-center mt-1">
          <span style={{ fontSize: 10, color: 'var(--feebbo-muted)' }}>
            {getFileType(file.name)}
          </span>
          <span style={{ fontSize: 10, color: 'var(--feebbo-muted)' }}>
            {formatBytes(file.size)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── List row ─────────────────────────────────────────────────────
interface RowProps {
  item: DriveFile | DriveFolder;
  isFolder: boolean;
  selected: boolean;
  onOpen: () => void;
  onSelect: (e: React.MouseEvent) => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  isDragOver?: boolean;
}

function ListRow({ item, isFolder, selected, onOpen, onSelect, onContextMenu, onCheckboxChange, onDragStart, onDragEnd, onDragOver, onDragLeave, onDrop, isDragOver }: RowProps) {
  const file = item as DriveFile;
  const isImage = !isFolder && isImageFile(item.name);
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);
  const [thumbError, setThumbError] = useState(false);

  const loadThumb = async () => {
    if (!isImage || thumbUrl || thumbError) return;
    try {
      const url = await api.getDownloadUrl(file.path);
      setThumbUrl(url);
    } catch (e) {
      setThumbError(true);
    }
  };

  return (
    <tr
      className={`${selected ? 'selected-row' : ''} ${isDragOver ? 'drag-over' : ''}`}
      style={{ cursor: 'pointer', userSelect: 'none', color: 'var(--feebbo-text)', outline: isDragOver ? '2px dashed #2563eb' : 'none' }}
      data-path={item.path}
      data-item
      draggable
      onClick={onSelect}
      onDoubleClick={onOpen}
      onContextMenu={onContextMenu}
      onMouseEnter={loadThumb}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <td style={{ width: 40 }} onClick={e => e.stopPropagation()}>
        <input
          type="checkbox"
          className="form-check-input"
          checked={selected}
          onChange={onCheckboxChange}
          style={{ cursor: 'pointer' }}
        />
      </td>
      <td style={{ width: 56 }}>
        {isFolder ? (
          <i className="bi bi-folder-fill" style={{ color: '#f0ad4e', fontSize: 18 }} />
        ) : isImage && thumbUrl && !thumbError ? (
          <img
            src={thumbUrl}
            alt={item.name}
            style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 4 }}
            onError={() => setThumbError(true)}
          />
        ) : (
          <i className={`bi ${fileIcon(item.name)}`} style={{ fontSize: 18 }} />
        )}
      </td>
      <td className="fw-medium small">{item.name}</td>
      <td className="text-body-secondary small">{isFolder ? '—' : formatBytes(file.size)}</td>
      <td className="text-body-secondary small">{isFolder ? '—' : formatDate(file.lastModified)}</td>
    </tr>
  );
}

// ─── Main component ───────────────────────────────────────────────
interface Props {
  folders: DriveFolder[];
  files: DriveFile[];
  selected: Set<string>;
  viewMode: 'grid' | 'list';
  onFolderOpen: (folder: DriveFolder) => void;
  onFileDownload: (path: string) => void;
  onFileOpen: (path: string) => void;
  onSelect: (path: string, multi?: boolean) => void;
  onContextMenu: (e: React.MouseEvent, item: DriveFile | DriveFolder, type: 'file' | 'folder') => void;
  onMoveItems?: (targetFolderPath: string, sourcePaths: string[]) => void;
}

export default function FileGrid({
  folders, files, selected, viewMode,
  onFolderOpen, onFileDownload, onFileOpen, onSelect, onContextMenu, onMoveItems,
}: Props) {
  // ─── Drag & drop state ────────────────────────────────────────────
  const [dragOverFolder, setDragOverFolder] = useState<string | null>(null);

  const handleDragStart = (e: React.DragEvent, path: string) => {
    // Si el item arrastrado está en la selección, arrastrar todos los seleccionados
    // Si no, arrastrar solo este item
    const paths = selected.has(path) ? Array.from(selected) : [path];
    e.dataTransfer.setData('application/json', JSON.stringify(paths));
    e.dataTransfer.effectAllowed = 'move';
    // Añadir clase visual para indicar que se está arrastrando
    (e.currentTarget as HTMLElement).style.opacity = '0.5';
  };

  const handleFolderDragOver = (e: React.DragEvent, folderPath: string) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverFolder(folderPath);
  };

  const handleFolderDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFolder(null);
  };

  const handleFolderDrop = (e: React.DragEvent, targetFolderPath: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFolder(null);
    try {
      const data = e.dataTransfer.getData('application/json');
      if (data && onMoveItems) {
        const sourcePaths: string[] = JSON.parse(data);
        // No mover una carpeta dentro de sí misma o dentro de sus descendientes
        const filtered = sourcePaths.filter(p => {
          const clean = p.replace(/\/$/, '');
          const cleanTarget = targetFolderPath.replace(/\/$/, '');
          // No mover si es la misma carpeta o si target está dentro de source
          return clean !== cleanTarget && !cleanTarget.startsWith(clean + '/');
        });
        if (filtered.length > 0) {
          onMoveItems(targetFolderPath, filtered);
        }
      }
    } catch (err) {
      console.error('Error handling drop:', err);
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    // Restaurar opacidad al terminar el drag
    (e.currentTarget as HTMLElement).style.opacity = '1';
  };

  // ─── Marquee selection (click-drag) ─────────────────────────────
  const containerRef = useRef<HTMLDivElement | null>(null);
  const anchorIndexRef = useRef<number | null>(null);

  // Mapa de índice de visualización para selección por rango
  const displayItems = [
    ...folders.map(f => ({ path: f.path })),
    ...files.map(f => ({ path: f.path })),
  ];
  const indexByPath = new Map<string, number>(displayItems.map((it, i) => [it.path, i]));

  const handleItemClick = (e: React.MouseEvent, path: string) => {
    e.stopPropagation();
    const idx = indexByPath.get(path) ?? null;

    if (e.shiftKey && idx !== null) {
      const anchor = anchorIndexRef.current ?? idx;
      const [start, end] = [Math.min(anchor, idx), Math.max(anchor, idx)];
      const range = displayItems.slice(start, end + 1).map(it => it.path);

      if (range.length > 0) {
        if (e.ctrlKey || (e as any).metaKey) {
          // Añadir rango a la selección existente
          range.forEach(p => onSelect(p, true));
        } else {
          // Reemplazar por el rango
          onSelect(range[0], false);
          for (let i = 1; i < range.length; i++) onSelect(range[i], true);
        }
      }
      anchorIndexRef.current = idx;
      return;
    }

    // Sin shift: simple o toggle
    if (idx !== null) anchorIndexRef.current = idx;
    const multi = e.ctrlKey || (e as any).metaKey;
    onSelect(path, multi);
  };

  const applySelection = useCallback((keys: string[], mode: 'replace' | 'add') => {
    if (!keys.length) return;
    if (mode === 'replace') {
      onSelect(keys[0], false);
      for (let i = 1; i < keys.length; i++) onSelect(keys[i], true);
    } else {
      for (let i = 0; i < keys.length; i++) onSelect(keys[i], true);
    }
  }, [onSelect]);

  const { box: marquee, overlayStyle } = useMarqueeSelection(
    containerRef,
    applySelection,
    {
      itemSelector: '[data-item]',
      isInteractive: (el) => {
        // Ignorar si es un elemento interactivo o si está sobre un item draggable
        if (el.closest('button, a, input, textarea, select, [role="button"], .form-check-input, .dropdown, .btn')) {
          return true;
        }
        // Si el elemento o su ancestro directo es draggable, considerarlo interactivo
        // para evitar conflicto entre marquee y drag-to-move
        const draggableParent = el.closest('[draggable="true"]');
        if (draggableParent) {
          return true;
        }
        return false;
      },
      dragThreshold: 2,
    }
  );

  // Select-all logic for visible items (used in list view)
  const visiblePaths = [
    ...folders.map(f => f.path),
    ...files.map(f => f.path),
  ];
  const allSelected = visiblePaths.length > 0 && visiblePaths.every(p => selected.has(p));
  const someSelected = visiblePaths.some(p => selected.has(p));

  const headerCheckboxRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = !allSelected && someSelected;
    }
  }, [allSelected, someSelected]);

  const onToggleSelectAll = () => {
    if (!someSelected) {
      // Select all visible: replace selection with visible subset
      if (visiblePaths.length === 0) return;
      onSelect(visiblePaths[0], false);
      for (let i = 1; i < visiblePaths.length; i++) onSelect(visiblePaths[i], true);
    } else if (allSelected) {
      // Deselect all visible: toggle-off each visible selected
      for (const p of visiblePaths) {
        if (selected.has(p)) onSelect(p, true);
      }
    } else {
      // Some selected: complete to all
      const toAdd = visiblePaths.filter(p => !selected.has(p));
      if (toAdd.length) {
        // preserve existing selection, just add missing
        for (let i = 0; i < toAdd.length; i++) onSelect(toAdd[i], true);
      }
    }
  };

  if (viewMode === 'list') {
    return (
      <>
      <div className="table-responsive" ref={containerRef} style={{height: '86vh', touchAction: 'none' }}>
        <table className="table feebbo-table align-middle mb-0">
          <thead>
            <tr>
              <th style={{ width: 40 }}>
                <input
                  ref={headerCheckboxRef}
                  type="checkbox"
                  className="form-check-input"
                  checked={allSelected}
                  onChange={onToggleSelectAll}
                />
              </th>
              <th style={{ width: 56 }} />
              <th>Nombre</th>
              <th style={{ width: 100 }}>Tamaño</th>
              <th style={{ width: 130 }}>Modificado</th>
            </tr>
          </thead>
          <tbody>
            {folders.map(f => (
              <ListRow
                key={f.path} item={f} isFolder
                selected={selected.has(f.path)}
                onOpen={() => onFolderOpen(f)}
                onSelect={e => handleItemClick(e, f.path)}
                onContextMenu={e => onContextMenu(e, f, 'folder')}
                onCheckboxChange={() => onSelect(f.path, true)}
                onDragStart={e => handleDragStart(e, f.path)}
                onDragEnd={handleDragEnd}
                onDragOver={e => handleFolderDragOver(e, f.path)}
                onDragLeave={handleFolderDragLeave}
                onDrop={e => handleFolderDrop(e, f.path)}
                isDragOver={dragOverFolder === f.path}
              />
            ))}
            {files.map(f => (
              <ListRow
                key={f.path} item={f} isFolder={false}
                selected={selected.has(f.path)}
                onOpen={() => onFileOpen(f.path)}
                onSelect={e => handleItemClick(e, f.path)}
                onContextMenu={e => onContextMenu(e, f, 'file')}
                onCheckboxChange={() => onSelect(f.path, true)}
                onDragStart={e => handleDragStart(e, f.path)}
                onDragEnd={handleDragEnd}
              />
            ))}
          </tbody>
        </table>
      </div>
      {marquee.active && (<div style={overlayStyle} />)}
      </>
    );
  }

  // Grid view
  return (
    <>
    <div ref={containerRef} style={{ height: '86vh',position: 'relative', touchAction: 'none' }}>
      {folders.length > 0 && (
        <>
          <p className="small fw-semibold text-uppercase mb-2" style={{ letterSpacing: '0.5px', color: 'var(--feebbo-muted)' }}>
            Carpetas
          </p>
          <div className="row row-cols-2 row-cols-sm-3 row-cols-md-4 row-cols-lg-5 g-3 mb-4">
            {folders.map(f => (
              <div key={f.path} data-path={f.path}>
                <FolderCard
                  folder={f}
                  selected={selected.has(f.path)}
                  onOpen={() => onFolderOpen(f)}
                  onSelect={e => handleItemClick(e, f.path)}
                  onContextMenu={e => onContextMenu(e, f, 'folder')}
                  onDragStart={e => handleDragStart(e, f.path)}
                  onDragEnd={handleDragEnd}
                  onDragOver={e => handleFolderDragOver(e, f.path)}
                  onDragLeave={handleFolderDragLeave}
                  onDrop={e => handleFolderDrop(e, f.path)}
                  isDragOver={dragOverFolder === f.path}
                />
              </div>
            ))}
          </div>
        </>
      )}

      {files.length > 0 && (
        <>
          <p className="small fw-semibold text-uppercase mb-2" style={{ letterSpacing: '0.5px', color: 'var(--feebbo-muted)' }}>
            Archivos
          </p>
          <div className="row row-cols-2 row-cols-sm-3 row-cols-md-4 row-cols-lg-5 g-3">
            {files.map(f => (
              <div key={f.path} className="col" data-path={f.path}>
                <FileCard
                  file={f}
                  selected={selected.has(f.path)}
                  onDownload={() => onFileDownload(f.path)}
                  onOpen={() => onFileOpen(f.path)}
                  onSelect={e => handleItemClick(e, f.path)}
                  onContextMenu={e => onContextMenu(e, f, 'file')}
                  onDragStart={e => handleDragStart(e, f.path)}
                  onDragEnd={handleDragEnd}
                />
              </div>
            ))}
          </div>
        </>
      )}

    </div>
    
    {/* Marquee selection box - rendered at root level */}
    {marquee.active && (
      <div style={overlayStyle} />
    )}
    </>
  );
}
